import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useItemDetails, useUpdateItem, useCategories, useLocations } from '../hooks/useItems'
import MoveSlideout from '../components/MoveSlideout'
import SoldSlideout from '../components/SoldSlideout'
import PicklistSlideout from '../components/PicklistSlideout'
import ListedSlideout from '../components/ListedSlideout'
import ExportModal from '../components/ExportModal'
import ReturnItemModal from '../components/returns/ReturnItemModal'
import PrintButton from '../components/print/PrintButton'
import { getImageUrl } from '../utils/imageUtils'
import {
  ArrowLeft,
  Edit,
  Edit2,
  Save,
  X,
  Camera,
  Package,
  DollarSign,
  MapPin,
  Tag,
  History,
  Image as ImageIcon,
  Plus,
  Trash2,
  Move,
  Printer,
  Download,
  RefreshCw,
  AlertCircle,
  QrCode,
  Copy,
  Star,
  Bot,
  ShoppingCart,
  RotateCcw,
  TrendingUp,
  Activity
} from 'lucide-react'

interface Item {
  id: string
  name: string
  sku: string
  ic: string
  description: string
  condition: string
  status: string
  quantity: number
  reserved: number
  target_price: number
  sale_price: number
  cost: number
  created_at: string
  updated_at: string
  category_name: string
  category_id: string
  location_name: string
  location_id: string
  shelf_name: string
  shelf_id: string
  tier_position: number
  tier_nickname: string
  tier_id: string
  tote_name: string
  tote_id: string
  brand?: string
  model?: string
  notes?: string
  weight_lbs?: number
  length_cm?: number
  width_cm?: number
  height_cm?: number
  shipping_instructions?: string
  package_type?: string
  source?: string
  acquisition_date?: string
  images?: string[]
  custom_fields?: Array<{
    name: string
    value: string
    type: string
  }>
  sales_history?: Array<{
    id: string
    item_id: string
    item_ic: string
    marketplace: string
    sold_for: number
    marketplace_fees: number
    shipping_cost: number
    cogs: number
    quantity_sold: number
    sale_date: string
    notes?: string
    net_profit: number
    created_at: string
    updated_at: string
  }>
  history?: Array<{
    id: string
    action: string
    user: string
    timestamp: string
    details: string
  }>
}

export default function ItemDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [isEditing, setIsEditing] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [primaryImageIndex, setPrimaryImageIndex] = useState(0)
  const [aiMarkedImages, setAiMarkedImages] = useState<number[]>([])
  const [showSalesHistory, setShowSalesHistory] = useState(false)
  const [showActionsHistory, setShowActionsHistory] = useState(false)
  const [showMoveSlideout, setShowMoveSlideout] = useState(false)
  const [showSoldSlideout, setShowSoldSlideout] = useState(false)
  const [showPicklistSlideout, setShowPicklistSlideout] = useState(false)
  const [showListedSlideout, setShowListedSlideout] = useState(false)
  const [showReturnModal, setShowReturnModal] = useState(false)
  const [returnModalSale, setReturnModalSale] = useState<any>(null)
  const [editingReturn, setEditingReturn] = useState<any>(null)

  // Returns state
  const [returns, setReturns] = useState<any[]>([])
  const [returnsLoading, setReturnsLoading] = useState(false)
  const [showReturnsHistory, setShowReturnsHistory] = useState(false)

  // Fetch item details using React Query
  const { data: itemData, isLoading, error } = useItemDetails(id || '')
  const updateItemMutation = useUpdateItem()
  const { data: categoriesData } = useCategories()
  const { data: locationsData } = useLocations()

  const item = itemData?.item || null
  const loading = isLoading

  // Fetch images from database
  const [images, setImages] = useState<any[]>([])
  const [imagesLoading, setImagesLoading] = useState(false)

  // Sales history state
  const [salesHistory, setSalesHistory] = useState<any[]>([])
  const [salesHistoryLoading, setSalesHistoryLoading] = useState(false)
  const [editingSaleId, setEditingSaleId] = useState<string | null>(null)
  const [editingSaleData, setEditingSaleData] = useState<any>(null)

  // Actions history state
  const [actionsHistory, setActionsHistory] = useState<any[]>([])
  const [actionsHistoryLoading, setActionsHistoryLoading] = useState(false)

  // AI Assistant state
  const [aiResponses, setAiResponses] = useState<any[]>([])
  const [selectedAiResponse, setSelectedAiResponse] = useState<any>(null)
  const [aiLoading, setAiLoading] = useState(false)

  // Export state
  const [exportModalOpen, setExportModalOpen] = useState(false)
  const [aiGenerating, setAiGenerating] = useState(false)
  const [aiAnalyzing, setAiAnalyzing] = useState(false)

  // Filmstrip scroll state
  const filmstripRef = useRef<HTMLDivElement>(null)
  const [canScrollUp, setCanScrollUp] = useState(false)
  const [canScrollDown, setCanScrollDown] = useState(false)

  // Category-dependent custom fields
  const [categoryFields, setCategoryFields] = useState<any[]>([])
  const [categoryFieldValues, setCategoryFieldValues] = useState<Record<string, any>>({})

  // Listings state
  const [listings, setListings] = useState<any[]>([])
  const [listingsLoading, setListingsLoading] = useState(false)

  // Image enhancement state
  const [enhanceModalOpen, setEnhanceModalOpen] = useState(false)
  const [selectedImagesForEnhancement, setSelectedImagesForEnhancement] = useState<string[]>([])
  const [enhanceOptions, setEnhanceOptions] = useState({
    autoCrop: true,
    enhance: true,
    removeBg: false,
    bgColor: 'white',
    bgTransparent: false
  })
  const [enhancing, setEnhancing] = useState(false)

  useEffect(() => {
    if (id) {
      setImagesLoading(true)
      fetch(`/api/items/${id}/images`)
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setImages(data.images || [])
            // Load AI-marked images from database
            const aiIndices = (data.images || [])
              .map((img: any, idx: number) => img.ai_designation ? idx : -1)
              .filter((idx: number) => idx !== -1)
            setAiMarkedImages(aiIndices)
          }
        })
        .catch(err => console.error('Error fetching images:', err))
        .finally(() => setImagesLoading(false))
    }
  }, [id])

  // Fetch sales history
  useEffect(() => {
    if (id) {
      setSalesHistoryLoading(true)
      fetch(`/api/items/${id}/sales-history`)
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setSalesHistory(data.salesHistory || [])
          }
        })
        .catch(err => console.error('Error fetching sales history:', err))
        .finally(() => setSalesHistoryLoading(false))
    }
  }, [id])

  // Fetch actions history
  useEffect(() => {
    if (id) {
      setActionsHistoryLoading(true)
      fetch(`/api/items/${id}/actions-history`)
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setActionsHistory(data.actionsHistory || [])
          }
        })
        .catch(err => console.error('Error fetching actions history:', err))
        .finally(() => setActionsHistoryLoading(false))
    }
  }, [id])

  // Fetch AI responses
  useEffect(() => {
    if (id) {
      setAiLoading(true)
      fetch(`/api/items/${id}/ai-responses`)
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setAiResponses(data.responses || [])
            // Set the most recent response as selected
            if (data.responses && data.responses.length > 0) {
              setSelectedAiResponse(data.responses[0])
            }
          }
        })
        .catch(err => console.error('Error fetching AI responses:', err))
        .finally(() => setAiLoading(false))
    }
  }, [id])

  // Fetch returns history
  const fetchReturns = () => {
    if (id) {
      setReturnsLoading(true)
      fetch(`/api/returns-pg?item_id=${id}`)
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setReturns(data.data || [])
          }
        })
        .catch(err => console.error('Error fetching returns:', err))
        .finally(() => setReturnsLoading(false))
    }
  }

  useEffect(() => {
    fetchReturns()
  }, [id])

  // Fetch listings
  const fetchListings = () => {
    if (id) {
      setListingsLoading(true)
      fetch(`/api/listings-pg?item_id=${id}`)
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setListings(data.listings || [])
          }
        })
        .catch(err => console.error('Error fetching listings:', err))
        .finally(() => setListingsLoading(false))
    }
  }

  useEffect(() => {
    fetchListings()
  }, [id])

  // Check for edit query parameter and enable edit mode when item is loaded
  useEffect(() => {
    if (searchParams.get('edit') === 'true' && item && !isEditing) {
      setIsEditing(true)
    }
  }, [searchParams, item, isEditing])

  // Form state for editing
  const [formData, setFormData] = useState<any>({})

  // Fetch category fields when category changes
  useEffect(() => {
    const categoryId = formData.category_id || item?.category_id
    if (categoryId) {
      fetch(`/api/central-optimized?op=category-fields&category_id=${categoryId}`)
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setCategoryFields(data.fields || [])
          }
        })
        .catch(err => console.error('Error fetching category fields:', err))
    } else {
      setCategoryFields([])
    }
  }, [formData.category_id, item?.category_id])

  // Initialize form data when item loads
  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name,
        sku: item.sku,
        ic: item.ic,
        description: item.description,
        condition: item.condition,
        status: item.status,
        quantity: item.quantity,
        target_price: item.target_price,
        sale_price: item.sale_price,
        cost: item.cost,
        category_id: item.category_id,
        location_id: item.location_id,
        tote_id: item.tote_id,
        brand: item.brand as any,
        model: item.model,
        notes: item.notes,
        weight_lbs: item.weight_lbs,
        length_cm: item.length_cm,
        width_cm: item.width_cm,
        height_cm: item.height_cm,
        shipping_instructions: item.shipping_instructions,
        package_type: item.package_type,
        source: item.source,
        acquisition_date: item.acquisition_date
      })
    }
  }, [item])

  const handleSave = () => {
    if (item) {
      const { images, ...dataToSave } = formData

      updateItemMutation.mutate(
        { id: item.id, data: dataToSave },
        {
          onSuccess: () => {
            setIsEditing(false)
            console.log('Item saved successfully')
          },
          onError: (error) => {
            console.error('Failed to save item:', error)
          }
        }
      )
    }
  }

  const handleCancel = () => {
    setIsEditing(false)
    if (item) {
      setFormData({
        name: item.name,
        sku: item.sku,
        ic: item.ic,
        description: item.description,
        condition: item.condition,
        status: item.status,
        quantity: item.quantity,
        target_price: item.target_price,
        sale_price: item.sale_price,
        cost: item.cost,
        category_id: item.category_id,
        location_id: item.location_id,
        tote_id: item.tote_id,
        brand: item.brand as any,
        model: item.model,
        notes: item.notes,
        weight_lbs: item.weight_lbs,
        length_cm: item.length_cm,
        width_cm: item.width_cm,
        height_cm: item.height_cm,
        shipping_instructions: item.shipping_instructions,
        package_type: item.package_type,
        source: item.source,
        acquisition_date: item.acquisition_date
      })
    }
  }

  const handleFieldChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    } as any))
  }

  // Image upload functions
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files && files.length > 0 && item) {
      try {
        const imagePromises = Array.from(files).map(async (file) => {
          return new Promise<string>((resolve) => {
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result as string)
            reader.readAsDataURL(file)
          })
        })

        const base64Images = await Promise.all(imagePromises)

        const response = await fetch(`/api/items/${item.id}/images`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            images: base64Images.map((dataUrl, index) => ({
              url: dataUrl,
              is_primary: index === 0,
              order_index: index,
              ai_designation: false
            }))
          })
        })

        if (response.ok) {
          const result = await response.json()
          console.log('Images uploaded successfully:', result)
          // Refresh images without exiting edit mode
          const imagesResponse = await fetch(`/api/items/${item.id}/images`)
          const imagesData = await imagesResponse.json()
          if (imagesData.success) {
            setImages(imagesData.images || [])
          }
        } else {
          console.error('Failed to upload images:', await response.text())
        }
      } catch (error) {
        console.error('Error uploading images:', error)
      }
    }
  }

  const handleCameraCapture = async () => {
    // Import camera utilities
    const { createCameraInput, fileToDataURL, checkCameraPermission, getPermissionInstructions, isSecureContext } = await import('../utils/cameraPermissions');

    // Check if we're in a secure context
    if (!isSecureContext()) {
      alert('Camera access requires a secure connection (HTTPS). Please use HTTPS or localhost.');
      return;
    }

    try {
      // Check permission status first
      const permissionStatus = await checkCameraPermission();

      if (permissionStatus.denied) {
        const instructions = getPermissionInstructions();
        alert(`Camera access is blocked.\n\n${instructions}\n\nAfter enabling, please refresh the page and try again.`);
        return;
      }

      // Create camera input with mobile support
      const input = createCameraInput(async (file) => {
        try {
          const dataURL = await fileToDataURL(file);
          // Simulate the event structure expected by handleImageUpload
          const fakeEvent = {
            target: {
              files: [file]
            }
          } as React.ChangeEvent<HTMLInputElement>;
          handleImageUpload(fakeEvent);
        } catch (error) {
          console.error('Error processing camera capture:', error);
          alert('Failed to process captured image. Please try again.');
        }
      }, { multiple: true });

      input.click();
    } catch (error) {
      console.error('Camera capture error:', error);
      alert('Failed to access camera. Please check your browser settings and try again.');
    }
  }

  const handleRemoveImage = async (imageId: string) => {
    if (!item || !isEditing) return

    if (!confirm('Are you sure you want to delete this image?')) return

    try {
      const response = await fetch(`/api/images/${imageId}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        console.log('Image deleted successfully')
        window.location.reload()
      } else {
        console.error('Failed to delete image:', await response.text())
      }
    } catch (error) {
      console.error('Error deleting image:', error)
    }
  }

  // Filmstrip scroll functions
  const checkScrollButtons = () => {
    if (filmstripRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = filmstripRef.current
      setCanScrollUp(scrollTop > 0)
      setCanScrollDown(scrollTop < scrollHeight - clientHeight - 5)
    }
  }

  const scrollFilmstrip = (direction: 'up' | 'down') => {
    if (filmstripRef.current) {
      const scrollAmount = 150
      filmstripRef.current.scrollBy({
        top: direction === 'down' ? scrollAmount : -scrollAmount,
        behavior: 'smooth'
      })
      setTimeout(checkScrollButtons, 300)
    }
  }

  // Sales history edit handlers
  const handleEditSale = (sale: any) => {
    setEditingSaleId(sale.id)
    setEditingSaleData({
      marketplace: sale.marketplace || '',
      sale_price: sale.sale_price || '',
      marketplace_fees: sale.marketplace_fees || '',
      shipping_fees: sale.shipping_fees || '',
      quantity_sold: sale.quantity_sold || 1,
      sale_date: sale.sale_date ? new Date(sale.sale_date).toISOString().split('T')[0] : '',
      notes: sale.notes || ''
    })
  }

  // Returns handlers
  const handleReturnItem = () => {
    setReturnModalSale(null)
    setShowReturnModal(true)
  }

  const handleReturnFromSale = (sale: any) => {
    setReturnModalSale(sale)
    setShowReturnModal(true)
  }

  const handleReturnSuccess = (returnData: any) => {
    console.log('Return created successfully:', returnData)
    fetchReturns() // Refresh returns list
    setShowReturnModal(false)
    setReturnModalSale(null)
    setEditingReturn(null)
  }

  const handleEditReturn = async (returnItem: any) => {
    try {
      // Fetch full return details with images
      const response = await fetch(`/api/returns-pg/${returnItem.id}`)
      const result = await response.json()

      if (result.success && result.data) {
        setEditingReturn(result.data)
        setShowReturnModal(true)
      }
    } catch (error) {
      console.error('Error fetching return details:', error)
    }
  }

  const handleCancelEditSale = () => {
    setEditingSaleId(null)
    setEditingSaleData(null)
  }

  const handleSaveSale = async (saleId: string) => {
    try {
      const response = await fetch(`/api/sales-history/${saleId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingSaleData)
      })

      if (!response.ok) {
        throw new Error('Failed to update sale')
      }

      const result = await response.json()
      if (result.success) {
        // Refresh sales history
        const salesResponse = await fetch(`/api/items/${id}/sales-history`)
        const salesData = await salesResponse.json()
        if (salesData.success) {
          setSalesHistory(salesData.salesHistory || [])
        }
        setEditingSaleId(null)
        setEditingSaleData(null)
      } else {
        throw new Error(result.error || 'Failed to update sale')
      }
    } catch (error) {
      console.error('Error updating sale:', error)
      alert(`Failed to update sale: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  const handleSaleFieldChange = (field: string, value: string) => {
    setEditingSaleData((prev: any) => ({
      ...prev,
      [field]: value
    }))
  }

  // Check scroll buttons when images change
  useEffect(() => {
    checkScrollButtons()
  }, [images])

  const handleCustomFieldChange = (index: number, value: string) => {
    if (item && item.custom_fields) {
      const updatedFields = [...item.custom_fields]
      updatedFields[index] = { ...updatedFields[index], value }
    }
  }

  const handleSetPrimary = async (imageId: string) => {
    if (!isEditing || !item) return // Only allow in edit mode

    // Optimistically update UI
    const updatedImages = images.map(img => ({
      ...img,
      is_primary: img.id === imageId
    }))
    setImages(updatedImages)

    try {
      // Send PATCH request with just the one image being set as primary
      const response = await fetch(`/api/items/${item.id}/images`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates: [{
            id: imageId,
            is_primary: true
          }]
        })
      })

      if (!response.ok) {
        throw new Error('Failed to set primary image')
      }

      const data = await response.json()
      console.log('Primary image set:', data)
    } catch (error) {
      console.error('Error setting primary image:', error)
      // Revert on error
      setImages(images)
    }
  }

  const handleToggleAI = async (index: number) => {
    if (!isEditing || !item) return // Only allow in edit mode

    const image = images[index]
    if (!image) return

    const newAiDesignation = !aiMarkedImages.includes(index)

    // Check limit
    if (newAiDesignation && aiMarkedImages.length >= 5) {
      return
    }

    // Update local state optimistically
    if (newAiDesignation) {
      setAiMarkedImages(prev => [...prev, index])
    } else {
      setAiMarkedImages(prev => prev.filter(i => i !== index))
    }

    // Persist to database
    try {
      const response = await fetch(`/api/items/${item.id}/images`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates: [{
            id: image.id,
            ai_designation: newAiDesignation
          }]
        })
      })

      if (!response.ok) {
        throw new Error('Failed to update AI designation')
      }

      const data = await response.json()
      console.log('AI designation updated:', data)
    } catch (error) {
      console.error('Error updating AI designation:', error)
      // Revert on error
      if (newAiDesignation) {
        setAiMarkedImages(prev => prev.filter(i => i !== index))
      } else {
        setAiMarkedImages(prev => [...prev, index])
      }
    }
  }

  // AI Assistant handlers
  const handleRegenerateDescription = async () => {
    if (!item?.id) return

    setAiGenerating(true)
    try {
      const response = await fetch('/api/ai/generate-listing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          item_id: item.id,
          regenerate: true
        })
      })

      const data = await response.json()

      if (data.success) {
        // Refresh AI responses
        const aiResponse = await fetch(`/api/items/${item.id}/ai-responses`)
        const aiData = await aiResponse.json()
        if (aiData.success) {
          setAiResponses(aiData.responses || [])
          if (aiData.responses && aiData.responses.length > 0) {
            setSelectedAiResponse(aiData.responses[0])
          }
        }
      } else {
        console.error('Failed to regenerate description:', data.error)
      }
    } catch (error) {
      console.error('Error regenerating description:', error)
    } finally {
      setAiGenerating(false)
    }
  }

  const handleAIAnalyze = async () => {
    if (!item?.id) return

    setAiAnalyzing(true)
    try {
      const response = await fetch('/api/ai/analyze-item', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_id: item.id })
      })

      const data = await response.json()

      if (data.success && data.data) {
        // Create a temporary AI response object from the analysis
        const tempResponse = {
          id: 'temp-' + Date.now(),
          item_id: item.id,
          response_text: data.data.analysis || 'No analysis available',
          model_version: 'gpt-4o',
          created_at: new Date().toISOString(),
          images_analyzed: data.data.images_analyzed || 0
        }

        setAiResponses([tempResponse])
        setSelectedAiResponse(tempResponse)

        // Also try to fetch persisted responses (if any)
        const aiResponse = await fetch(`/api/items/${item.id}/ai-responses`)
        const aiData = await aiResponse.json()
        if (aiData.success && aiData.responses && aiData.responses.length > 0) {
          setAiResponses(aiData.responses)
          setSelectedAiResponse(aiData.responses[0])
        }
      } else {
        console.error('Failed to analyze item:', data.error)
        alert('Failed to analyze item: ' + (data.error || 'Unknown error'))
      }
    } catch (error) {
      console.error('Error analyzing item:', error)
      alert('Error analyzing item: ' + (error instanceof Error ? error.message : 'Unknown error'))
    } finally {
      setAiAnalyzing(false)
    }
  }

  const handleCopyToRTF = () => {
    if (selectedAiResponse?.response_text) {
      navigator.clipboard.writeText(selectedAiResponse.response_text)
        .then(() => {
          console.log('Copied to clipboard')
          // TODO: Show toast notification
        })
        .catch(err => console.error('Failed to copy:', err))
    }
  }

  const handleDuplicate = async () => {
    if (!item?.id) return

    const confirmed = window.confirm(
      `Duplicate item ${item.ic}?\n\nThis will create a new item with a new IC code. Images will NOT be copied - you'll need to upload them separately.`
    )

    if (!confirmed) return

    try {
      const response = await fetch(`/api/items/${item.id}/duplicate`, {
        method: 'POST'
      })

      const data = await response.json()

      if (data.success) {
        // Navigate to the new duplicated item
        navigate(`/items/${data.item.id}`)
        // Reload to show new item
        window.location.href = `/items/${data.item.id}`
      } else {
        alert(`Failed to duplicate item: ${data.error}`)
      }
    } catch (error) {
      console.error('Error duplicating item:', error)
      alert('Failed to duplicate item. Please try again.')
    }
  }

  const handleEnhanceImages = async () => {
    if (!item?.id || selectedImagesForEnhancement.length === 0) {
      alert('Please select at least one image to enhance')
      return
    }

    setEnhancing(true)

    try {
      const response = await fetch('/api/images/enhance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          item_id: item.id,
          image_ids: selectedImagesForEnhancement,
          options: enhanceOptions
        })
      })

      const data = await response.json()

      console.log('Enhancement response:', data)

      if (data.success) {
        const successCount = data.summary.succeeded
        const failCount = data.summary.failed

        // Log errors if any
        if (data.errors && data.errors.length > 0) {
          console.error('Enhancement errors:', data.errors)
        }

        alert(`Image enhancement complete!\n\n✓ ${successCount} image(s) enhanced${failCount > 0 ? `\n✗ ${failCount} image(s) failed` : ''}${failCount > 0 && data.errors ? `\n\nErrors:\n${data.errors.map(e => `- ${e.error}`).join('\n')}` : ''}`)

        // Reload images to show enhanced versions
        const imagesResponse = await fetch(`/api/items/${item.id}/images`)
        const imagesData = await imagesResponse.json()
        if (imagesData.success) {
          setImages(imagesData.images || [])
        }

        // Close modal and reset selection
        setEnhanceModalOpen(false)
        setSelectedImagesForEnhancement([])
      } else {
        alert(`Failed to enhance images: ${data.error}`)
      }
    } catch (error) {
      console.error('Error enhancing images:', error)
      alert('Failed to enhance images. Please try again.')
    } finally {
      setEnhancing(false)
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px' }}>
        <RefreshCw style={{ width: '32px', height: '32px', color: '#a0a8b8' }} className="animate-spin" />
        <span style={{ marginLeft: '8px', color: '#E0E6F0' }}>Loading item details...</span>
      </div>
    )
  }

  if (error || !item) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <AlertCircle style={{ width: '48px', height: '48px', color: '#6b7280', margin: '0 auto 16px' }} />
        <h3 style={{ fontSize: '1.125rem', fontWeight: '500', color: '#E0E6F0', marginBottom: '8px' }}>
          {error ? 'Error Loading Item' : 'Item Not Found'}
        </h3>
        <p style={{ color: '#6b7280', marginBottom: '16px' }}>
          {error ? 'Failed to load item details. Please try again.' : 'The requested item could not be found.'}
        </p>
        <button
          onClick={() => navigate('/search')}
          style={{
            padding: '10px 20px',
            background: '#242938',
            border: '2px solid #f97316',
            color: '#E0E6F0',
            borderRadius: '6px',
            cursor: 'pointer',
            fontFamily: 'Rajdhani, sans-serif',
            fontWeight: '600',
            fontSize: '0.875rem',
            textTransform: 'uppercase',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <ArrowLeft style={{ width: '16px', height: '16px' }} />
          Back to Search
        </button>
      </div>
    )
  }

  return (
    <div style={{
      paddingBottom: '100px',
      padding: '24px',
      background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
      minHeight: '100vh',
      fontFamily: 'Rajdhani, sans-serif'
    }}>
      {/* Page Header */}
      <div style={{
        marginBottom: '32px',
        borderBottom: '2px solid #FF6B35',
        paddingBottom: '24px'
      }}>
        <h1 style={{
          fontFamily: 'Rajdhani, sans-serif',
          fontSize: '48px',
          fontWeight: 700,
          color: '#FF6B35',
          textTransform: 'uppercase',
          letterSpacing: '2px',
          margin: 0,
          textShadow: '0 0 20px rgba(255, 107, 53, 0.6)',
          marginBottom: '16px'
        }}>{item.name}</h1>
        <div style={{
          fontFamily: 'Rajdhani, sans-serif',
          fontSize: '16px',
          color: '#8A95AA',
          margin: '0 0 12px 0',
          letterSpacing: '1px'
        }}>
          SKU: {item.sku || 'N/A'} • IC: {item.ic} • Location: {item.location_name || 'N/A'} • Tote: {item.tote_name || 'N/A'}
        </div>

        {/* Marketplace Listings - Below SKU/IC */}
        {listings.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
            {listings.map((listing) => (
              <div
                key={listing.id}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  background: listing.status === 'active' ? 'rgba(34, 211, 238, 0.15)' : 'rgba(107, 114, 128, 0.15)',
                  border: `1px solid ${listing.status === 'active' ? 'rgba(34, 211, 238, 0.4)' : 'rgba(107, 114, 128, 0.4)'}`,
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  color: listing.status === 'active' ? '#22D3EE' : '#9ca3af'
                }}
              >
                <Tag style={{ width: 12, height: 12 }} />
                {listing.platform}
                {listing.price && (
                  <span style={{ color: '#10b981' }}>
                    ${parseFloat(listing.price).toFixed(2)}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Quick Stats Bar */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: '12px',
          marginBottom: '20px'
        }}>
          {[
            { label: 'STATUS', value: item.status.toUpperCase(), color: '#00FF00' },
            { label: 'CONDITION', value: item.condition.toUpperCase(), color: '#00F0FF' },
            { label: 'QUANTITY', value: item.quantity, color: '#FFD93D' },
            { label: 'TARGET', value: `$${item.target_price}`, color: '#00FF00' }
          ].map((stat, idx) => (
            <div key={idx} style={{
              background: 'linear-gradient(135deg, rgba(255, 107, 53, 0.05), rgba(255, 107, 53, 0.02))',
              border: '1px solid rgba(255, 107, 53, 0.3)',
              borderRadius: '6px',
              padding: '12px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '11px', color: '#8A95AA', letterSpacing: '1px', marginBottom: '4px' }}>
                {stat.label}
              </div>
              <div style={{ fontSize: '18px', fontWeight: 700, color: stat.color }}>
                {stat.value}
              </div>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: 'rgba(138, 149, 170, 0.2)',
              border: '2px solid #8A95AA',
              borderRadius: '6px',
              padding: '12px 24px',
              color: '#E0E6F0',
              fontSize: '14px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'Rajdhani, sans-serif'
            }}
          >
            <ArrowLeft style={{ width: 16, height: 16 }} />
            BACK
          </button>
          {!isEditing ? (
            <>
              <button
                onClick={() => setIsEditing(true)}
                style={{
                  background: 'linear-gradient(135deg, #FF6B35, #F97316)',
                  border: '2px solid #FF6B35',
                  borderRadius: '6px',
                  padding: '12px 24px',
                  color: '#FFFFFF',
                  fontSize: '14px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Rajdhani, sans-serif',
                  boxShadow: '0 0 20px rgba(255, 107, 53, 0.4)'
                }}
              >
                <Edit style={{ width: 16, height: 16 }} />
                EDIT
              </button>
              <button
                onClick={() => setShowMoveSlideout(true)}
                style={{
                  background: 'rgba(0, 240, 255, 0.2)',
                  border: '2px solid #00F0FF',
                  borderRadius: '6px',
                  padding: '12px 24px',
                  color: '#00F0FF',
                  fontSize: '14px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Rajdhani, sans-serif'
                }}
              >
                <Move style={{ width: 16, height: 16 }} />
                MOVE
              </button>
              <button
                onClick={() => setShowSoldSlideout(true)}
                style={{
                  background: 'rgba(255, 215, 0, 0.2)',
                  border: '2px solid #FFD700',
                  borderRadius: '6px',
                  padding: '12px 24px',
                  color: '#FFD700',
                  fontSize: '14px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Rajdhani, sans-serif'
                }}
              >
                <ShoppingCart style={{ width: 16, height: 16 }} />
                MARK SOLD
              </button>
              <button
                onClick={() => setShowListedSlideout(true)}
                style={{
                  background: 'rgba(34, 211, 238, 0.2)',
                  border: '2px solid #22D3EE',
                  borderRadius: '6px',
                  padding: '12px 24px',
                  color: '#22D3EE',
                  fontSize: '14px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Rajdhani, sans-serif'
                }}
              >
                <Tag style={{ width: 16, height: 16 }} />
                MARK LISTED
              </button>
            </>
          ) : null}
        </div>
      </div>

      {/* Images Section */}
      <div style={{
        background: 'linear-gradient(to bottom right, rgba(17, 24, 39, 0.95), rgba(168, 85, 247, 0.05), rgba(17, 24, 39, 0.95))',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(168, 85, 247, 0.2)',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '20px',
        position: 'relative',
        boxShadow: '0 0 30px rgba(168, 85, 247, 0.2)',
        transition: 'all 0.3s ease'
      }}>
        {/* Corner Accent */}
        <div style={{ position: 'absolute', top: 0, right: 0, width: '128px', height: '128px', pointerEvents: 'none', background: 'linear-gradient(to bottom right, transparent, transparent, rgba(168, 85, 247, 0.1))' }} />

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '24px',
          paddingBottom: '16px',
          borderBottom: '1px solid rgba(168, 85, 247, 0.2)'
        }}>
          <div style={{ padding: '12px', borderRadius: '8px', background: 'rgba(168, 85, 247, 0.1)', border: '1px solid rgba(168, 85, 247, 0.3)', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
            <ImageIcon style={{ width: '28px', height: '28px', color: '#a855f7' }} />
          </div>
          <h2 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#c084fc', margin: 0, letterSpacing: '0.025em' }}>Images</h2>
        </div>

        {isEditing && (
          <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
            <button
              onClick={() => document.getElementById('image-upload')?.click()}
              style={{
                padding: '10px 20px',
                background: 'rgba(168, 85, 247, 0.1)',
                border: '1px solid rgba(168, 85, 247, 0.3)',
                color: '#E0E6F0',
                borderRadius: '6px',
                cursor: 'pointer',
                fontFamily: 'Rajdhani, sans-serif',
                fontWeight: '600',
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(168, 85, 247, 0.2)'
                e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.5)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(168, 85, 247, 0.1)'
                e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.3)'
              }}
            >
              Upload Images
            </button>
            <button
              onClick={handleCameraCapture}
              style={{
                padding: '10px 20px',
                background: 'rgba(168, 85, 247, 0.1)',
                border: '1px solid rgba(168, 85, 247, 0.3)',
                color: '#E0E6F0',
                borderRadius: '6px',
                cursor: 'pointer',
                fontFamily: 'Rajdhani, sans-serif',
                fontWeight: '600',
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(168, 85, 247, 0.2)'
                e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.5)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(168, 85, 247, 0.1)'
                e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.3)'
              }}
            >
              Take Photo
            </button>
            {images && images.length > 0 && (
              <button
                onClick={() => setEnhanceModalOpen(true)}
                style={{
                  padding: '10px 20px',
                  background: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  color: '#10B981',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontWeight: '600',
                  fontSize: '0.875rem',
                  textTransform: 'uppercase',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(16, 185, 129, 0.2)'
                  e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.5)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(16, 185, 129, 0.1)'
                  e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.3)'
                }}
              >
                <Activity style={{ width: '16px', height: '16px' }} />
                Enhance Images
              </button>
            )}
            <input
              id="image-upload"
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              style={{ display: 'none' }}
            />
          </div>
        )}

        {images && images.length > 0 ? (
          <div style={{ display: 'flex', gap: '20px' }}>
            {/* Primary Image Area */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                position: 'relative',
                background: '#1a1f2e',
                border: '3px solid #2a3142',
                borderRadius: '8px',
                overflow: 'hidden',
                aspectRatio: '4/3'
              }}>
                <img
                  data-testid="primary-image"
                  src={getImageUrl(images[primaryImageIndex]?.medium_url || images[primaryImageIndex]?.url)}
                  alt="Primary Image"
                  onLoad={(e) => {
                    const img = e.currentTarget;
                    console.log('✅ Primary image loaded:', img.src, `${img.naturalWidth}x${img.naturalHeight}px`);
                  }}
                  onError={(e) => {
                    const img = e.currentTarget;
                    console.error('❌ Primary image failed to load:', img.src);
                    if (img.src !== '/placeholder.png') {
                      img.src = '/placeholder.png';
                    }
                  }}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                />
                {images[primaryImageIndex]?.is_primary && (
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    left: '12px',
                    background: '#f97316',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    fontWeight: '700',
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    Primary Image
                  </div>
                )}
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  background: '#06b6d4',
                  color: 'white',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontWeight: '700',
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  AI: {aiMarkedImages.length}/5
                </div>
              </div>
            </div>

            {/* Filmstrip */}
            <div style={{ position: 'relative', width: '140px' }}>
              {/* Scroll Up Button */}
              {images.length >= 5 && canScrollUp && (
                <button
                  onClick={() => scrollFilmstrip('up')}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '40px',
                    background: 'linear-gradient(180deg, rgba(26, 31, 46, 0.95) 0%, rgba(26, 31, 46, 0) 100%)',
                    border: 'none',
                    color: '#f97316',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#ff6b00'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#f97316'}
                >
                  ▲
                </button>
              )}

              <div
                ref={filmstripRef}
                onScroll={checkScrollButtons}
                className="filmstrip-scroll"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  maxHeight: '596px',
                  overflowY: 'auto',
                  padding: '4px',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none'
                }}
              >
                {images.map((image, index) => (
                  <div
                    key={image.id}
                    onClick={() => setPrimaryImageIndex(index)}
                    style={{
                      position: 'relative',
                      width: '140px',
                      height: '140px',
                      minHeight: '140px',
                      flexShrink: 0,
                      background: '#1a1f2e',
                      border: (image.is_primary || index === primaryImageIndex)
                        ? '3px solid #f97316'
                        : '2px solid #2a3142',
                      borderRadius: '6px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      boxShadow: (image.is_primary || index === primaryImageIndex)
                        ? '0 0 20px rgba(249, 115, 22, 0.4)'
                        : 'none'
                    }}
                  >
                    <img
                      data-testid="item-thumbnail"
                      src={getImageUrl(image.thumbnail_url || image.url)}
                      alt={`Thumbnail ${index + 1}`}
                      onError={(e) => {
                        const img = e.currentTarget;
                        console.error('❌ Thumbnail failed to load:', img.src);
                        if (img.src !== '/placeholder.png') {
                          img.src = '/placeholder.png';
                        }
                      }}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        pointerEvents: 'none'
                      }}
                    />

                    {/* Badges */}
                    <div style={{
                      position: 'absolute',
                      top: '4px',
                      right: '4px',
                      display: 'flex',
                      gap: '4px'
                    }}>
                      {image.is_primary && (
                        <div style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          background: '#f97316',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px'
                        }}>
                          ⭐
                        </div>
                      )}
                      {aiMarkedImages.includes(index) && (
                        <div style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          background: '#06b6d4',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px'
                        }}>
                          🤖
                        </div>
                      )}
                    </div>

                    {/* Overlay buttons - only show in edit mode */}
                    {isEditing && (
                      <div
                        className="thumbnail-overlay"
                        style={{
                          position: 'absolute',
                          inset: 0,
                          background: 'rgba(0, 0, 0, 0.8)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                          opacity: 0,
                          transition: 'opacity 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = '0'}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleSetPrimary(image.id)
                          }}
                          style={{
                            padding: '4px 8px',
                            background: '#242938',
                            border: '1px solid #f97316',
                            color: '#E0E6F0',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.75rem'
                          }}
                          title="Set as primary"
                        >
                          ⭐
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleToggleAI(index)
                          }}
                          style={{
                            padding: '4px 8px',
                            background: '#242938',
                            border: '1px solid #06b6d4',
                            color: '#E0E6F0',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.75rem'
                          }}
                          title="Mark for AI"
                        >
                          🤖
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleRemoveImage(image.id)
                          }}
                          style={{
                            padding: '4px 8px',
                            background: '#242938',
                            border: '1px solid #ef4444',
                            color: '#E0E6F0',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.75rem'
                          }}
                          title="Delete image"
                        >
                          🗑️
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Scroll Down Button */}
              {images.length >= 5 && canScrollDown && (
                <button
                  onClick={() => scrollFilmstrip('down')}
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '40px',
                    background: 'linear-gradient(0deg, rgba(26, 31, 46, 0.95) 0%, rgba(26, 31, 46, 0) 100%)',
                    border: 'none',
                    color: '#f97316',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#ff6b00'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#f97316'}
                >
                  ▼
                </button>
              )}
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            <ImageIcon style={{ width: '48px', height: '48px', margin: '0 auto 15px', opacity: '0.5' }} />
            <p>No images available</p>
          </div>
        )}
      </div>

      {/* Basic Information */}
      <div style={{
        background: 'linear-gradient(to bottom right, rgba(17, 24, 39, 0.95), rgba(251, 146, 60, 0.05), rgba(17, 24, 39, 0.95))',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(251, 146, 60, 0.2)',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '20px',
        position: 'relative',
        boxShadow: '0 0 30px rgba(251, 146, 60, 0.2)',
        transition: 'all 0.3s ease'
      }}>
        {/* Corner Accent */}
        <div style={{ position: 'absolute', top: 0, right: 0, width: '128px', height: '128px', pointerEvents: 'none', background: 'linear-gradient(to bottom right, transparent, transparent, rgba(251, 146, 60, 0.1))' }} />

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '24px',
          paddingBottom: '16px',
          borderBottom: '1px solid rgba(251, 146, 60, 0.2)'
        }}>
          <div style={{ padding: '12px', borderRadius: '8px', background: 'rgba(251, 146, 60, 0.1)', border: '1px solid rgba(251, 146, 60, 0.3)', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
            <Package style={{ width: '28px', height: '28px', color: '#fb923c' }} />
          </div>
          <h2 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#fdba74', margin: 0, letterSpacing: '0.025em' }}>Basic Information</h2>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Item Name *</label>
            <input
              type="text"
              value={isEditing ? formData.name || '' : item.name}
              onChange={isEditing ? (e) => handleFieldChange('name', e.target.value) : undefined}
              readOnly={!isEditing}
              style={{
                padding: '10px 12px',
                background: isEditing ? 'rgba(36, 41, 56, 0.8)' : 'rgba(36, 41, 56, 0.4)',
                border: isEditing ? '1px solid rgba(251, 146, 60, 0.4)' : '1px solid rgba(107, 114, 128, 0.3)',
                borderRadius: '6px',
                color: '#E0E6F0',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>SKU</label>
            <input
              type="text"
              value={isEditing ? formData.sku || '' : item.sku}
              onChange={isEditing ? (e) => handleFieldChange('sku', e.target.value) : undefined}
              readOnly={!isEditing}
              style={{
                padding: '10px 12px',
                background: isEditing ? 'rgba(36, 41, 56, 0.8)' : 'rgba(36, 41, 56, 0.4)',
                border: isEditing ? '1px solid rgba(251, 146, 60, 0.4)' : '1px solid rgba(107, 114, 128, 0.3)',
                borderRadius: '6px',
                color: '#E0E6F0',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            />
          </div>

          {/* Marketplace Listings - Show in view mode only */}
          {!isEditing && listings.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Listed On</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {listings.map((listing) => (
                  <div
                    key={listing.id}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      background: listing.status === 'active' ? 'rgba(34, 211, 238, 0.1)' : 'rgba(107, 114, 128, 0.1)',
                      border: `1px solid ${listing.status === 'active' ? 'rgba(34, 211, 238, 0.3)' : 'rgba(107, 114, 128, 0.3)'}`,
                      borderRadius: '6px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      color: listing.status === 'active' ? '#22D3EE' : '#9ca3af'
                    }}
                  >
                    <Tag style={{ width: 12, height: 12 }} />
                    {listing.platform}
                    {listing.price && (
                      <span style={{ color: '#10b981' }}>
                        ${parseFloat(listing.price).toFixed(2)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>IC</label>
            <input
              type="text"
              value={item.ic}
              readOnly
              style={{
                padding: '10px 12px',
                background: 'rgba(36, 41, 56, 0.4)',
                border: '1px solid rgba(107, 114, 128, 0.3)',
                borderRadius: '6px',
                color: '#E0E6F0',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'not-allowed'
              }}
            />
            <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>IC is auto-generated and cannot be edited</span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Category *</label>
            {isEditing ? (
              <select
                value={formData.category_id || ''}
                onChange={(e) => handleFieldChange('category_id', e.target.value)}
                style={{
                  padding: '10px 12px',
                  background: 'rgba(36, 41, 56, 0.8)',
                  border: '1px solid rgba(251, 146, 60, 0.4)',
                  borderRadius: '6px',
                  color: '#E0E6F0',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
              >
                <option value="">Select Category</option>
                {categoriesData?.categories?.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={item.category_name || ''}
                readOnly
                style={{
                  padding: '10px 12px',
                  background: 'rgba(36, 41, 56, 0.4)',
                  border: '1px solid rgba(107, 114, 128, 0.3)',
                  borderRadius: '6px',
                  color: '#E0E6F0',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'not-allowed'
                }}
              />
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Condition *</label>
            {isEditing ? (
              <select
                value={formData.condition || ''}
                onChange={(e) => handleFieldChange('condition', e.target.value)}
                style={{
                  padding: '10px 12px',
                  background: 'rgba(36, 41, 56, 0.8)',
                  border: '1px solid rgba(251, 146, 60, 0.4)',
                  borderRadius: '6px',
                  color: '#E0E6F0',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
              >
                <option value="new">New</option>
                <option value="used">Used</option>
                <option value="used-good">Used - Good</option>
                <option value="used-fair">Used - Fair</option>
                <option value="used-poor">Used - Poor</option>
                <option value="for_parts">For Parts</option>
              </select>
            ) : (
              <input
                type="text"
                value={item.condition || ''}
                readOnly
                style={{
                  padding: '10px 12px',
                  background: 'rgba(36, 41, 56, 0.4)',
                  border: '1px solid rgba(107, 114, 128, 0.3)',
                  borderRadius: '6px',
                  color: '#E0E6F0',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'not-allowed'
                }}
              />
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status *</label>
            {isEditing ? (
              <select
                value={formData.status || ''}
                onChange={(e) => handleFieldChange('status', e.target.value)}
                style={{
                  padding: '10px 12px',
                  background: 'rgba(36, 41, 56, 0.8)',
                  border: '1px solid rgba(251, 146, 60, 0.4)',
                  borderRadius: '6px',
                  color: '#E0E6F0',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
              >
                <option value="ready">Ready</option>
                <option value="listed">Listed</option>
                <option value="sold">Sold</option>
                <option value="archived">Archived</option>
                <option value="damaged">Damaged</option>
                <option value="returned">Returned</option>
              </select>
            ) : (
              <input
                type="text"
                value={item.status || ''}
                readOnly
                style={{
                  padding: '10px 12px',
                  background: 'rgba(36, 41, 56, 0.4)',
                  border: '1px solid rgba(107, 114, 128, 0.3)',
                  borderRadius: '6px',
                  color: '#E0E6F0',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'not-allowed'
                }}
              />
            )}
          </div>

          <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Description</label>
            <textarea
              value={isEditing ? formData.description || '' : item.description || ''}
              onChange={isEditing ? (e) => handleFieldChange('description', e.target.value) : undefined}
              readOnly={!isEditing}
              rows={4}
              style={{
                padding: '10px 12px',
                background: isEditing ? 'rgba(36, 41, 56, 0.8)' : 'rgba(36, 41, 56, 0.4)',
                border: isEditing ? '1px solid rgba(251, 146, 60, 0.4)' : '1px solid rgba(107, 114, 128, 0.3)',
                borderRadius: '6px',
                color: '#E0E6F0',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            />
          </div>
        </div>
      </div>

      {/* Custom Fields */}
      {categoryFields.length > 0 && (
        <div style={{
          background: 'linear-gradient(to bottom right, rgba(17, 24, 39, 0.95), rgba(251, 146, 60, 0.05), rgba(17, 24, 39, 0.95))',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(251, 146, 60, 0.2)',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '20px',
          position: 'relative',
          boxShadow: '0 0 30px rgba(251, 146, 60, 0.2)',
          transition: 'all 0.3s ease'
        }}>
          {/* Corner Accent */}
          <div style={{ position: 'absolute', top: 0, right: 0, width: '128px', height: '128px', pointerEvents: 'none', background: 'linear-gradient(to bottom right, transparent, transparent, rgba(251, 146, 60, 0.1))' }} />

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '24px',
            paddingBottom: '16px',
            borderBottom: '1px solid rgba(251, 146, 60, 0.2)'
          }}>
            <div style={{ padding: '12px', borderRadius: '8px', background: 'rgba(251, 146, 60, 0.1)', border: '1px solid rgba(251, 146, 60, 0.3)', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
              <Tag style={{ width: '28px', height: '28px', color: '#fb923c' }} />
            </div>
            <h2 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#fdba74', margin: 0, letterSpacing: '0.025em' }}>Category Fields</h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
            {categoryFields.map((field, index) => (
              <div key={field.id} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {field.field_name}
                  {field.required && <span style={{ color: '#fb923c', marginLeft: '4px' }}>*</span>}
                </label>
                {field.field_type === 'select' ? (
                  <select
                    value={categoryFieldValues[field.field_name] || ''}
                    onChange={(e) => setCategoryFieldValues(prev => ({ ...prev, [field.field_name]: e.target.value }))}
                    disabled={!isEditing}
                    required={field.required}
                    style={{
                      padding: '10px 12px',
                      background: isEditing ? 'rgba(36, 41, 56, 0.8)' : 'rgba(36, 41, 56, 0.4)',
                      border: isEditing ? '1px solid rgba(251, 146, 60, 0.4)' : '1px solid rgba(107, 114, 128, 0.3)',
                      borderRadius: '6px',
                      color: '#E0E6F0',
                      fontFamily: 'Rajdhani, sans-serif',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      cursor: isEditing ? 'pointer' : 'not-allowed'
                    }}
                  >
                    <option value="">Select {field.field_name}</option>
                    {field.options && field.options.map((option: string) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                ) : field.field_type === 'boolean' ? (
                  <div style={{ display: 'flex', alignItems: 'center', paddingTop: '4px' }}>
                    <input
                      type="checkbox"
                      checked={categoryFieldValues[field.field_name] === 'true' || categoryFieldValues[field.field_name] === true}
                      onChange={(e) => setCategoryFieldValues(prev => ({ ...prev, [field.field_name]: e.target.checked.toString() }))}
                      disabled={!isEditing}
                      style={{
                        width: '18px',
                        height: '18px',
                        cursor: isEditing ? 'pointer' : 'not-allowed'
                      }}
                    />
                  </div>
                ) : (
                  <input
                    type={field.field_type === 'number' ? 'number' : field.field_type === 'date' ? 'date' : 'text'}
                    value={categoryFieldValues[field.field_name] || ''}
                    onChange={(e) => setCategoryFieldValues(prev => ({ ...prev, [field.field_name]: e.target.value }))}
                    readOnly={!isEditing}
                    required={field.required}
                    style={{
                      padding: '10px 12px',
                      background: isEditing ? 'rgba(36, 41, 56, 0.8)' : 'rgba(36, 41, 56, 0.4)',
                      border: isEditing ? '1px solid rgba(251, 146, 60, 0.4)' : '1px solid rgba(107, 114, 128, 0.3)',
                      borderRadius: '6px',
                      color: '#E0E6F0',
                      fontFamily: 'Rajdhani, sans-serif',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      cursor: isEditing ? 'text' : 'not-allowed'
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Assistant */}
      <div style={{
        background: 'linear-gradient(to bottom right, rgba(17, 24, 39, 0.95), rgba(236, 72, 153, 0.05), rgba(17, 24, 39, 0.95))',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(236, 72, 153, 0.2)',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '20px',
        position: 'relative',
        boxShadow: '0 0 30px rgba(236, 72, 153, 0.2)',
        transition: 'all 0.3s ease'
      }}>
        {/* Corner Accent */}
        <div style={{ position: 'absolute', top: 0, right: 0, width: '128px', height: '128px', pointerEvents: 'none', background: 'linear-gradient(to bottom right, transparent, transparent, rgba(236, 72, 153, 0.1))' }} />

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '24px',
          paddingBottom: '16px',
          borderBottom: '1px solid rgba(236, 72, 153, 0.2)'
        }}>
          <div style={{ padding: '12px', borderRadius: '8px', background: 'rgba(236, 72, 153, 0.1)', border: '1px solid rgba(236, 72, 153, 0.3)', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
            <Bot style={{ width: '28px', height: '28px', color: '#ec4899' }} />
          </div>
          <h2 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#f9a8d4', margin: 0, letterSpacing: '0.025em' }}>AI Assistant</h2>
        </div>

        <div style={{
          background: 'rgba(36, 41, 56, 0.5)',
          border: '2px solid #2a3142',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '15px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontWeight: '600', color: 'white' }}>AI Generated Information</span>
              {aiResponses.length > 0 && (
                <span style={{
                  fontSize: '0.75rem',
                  color: '#a0a8b8',
                  background: 'rgba(249, 115, 22, 0.1)',
                  padding: '4px 10px',
                  borderRadius: '4px',
                  border: '1px solid rgba(249, 115, 22, 0.3)'
                }}>
                  {aiResponses.length} version{aiResponses.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <button
              onClick={handleCopyToRTF}
              disabled={!selectedAiResponse}
              style={{
                padding: '10px 20px',
                background: '#242938',
                border: '2px solid #f97316',
                color: '#E0E6F0',
                borderRadius: '6px',
                cursor: selectedAiResponse ? 'pointer' : 'not-allowed',
                fontFamily: 'Rajdhani, sans-serif',
                fontWeight: '600',
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                opacity: selectedAiResponse ? 1 : 0.5
              }}
            >
              Copy to RTF
            </button>
          </div>
          <div style={{ color: '#a0a8b8', fontSize: '0.875rem', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
            {aiLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <RefreshCw style={{ width: '16px', height: '16px' }} className="animate-spin" />
                <span>Loading AI responses...</span>
              </div>
            ) : selectedAiResponse ? (
              selectedAiResponse.response_text
            ) : (
              'No AI-generated information available. Use the buttons below to generate content.'
            )}
          </div>
          {aiResponses.length > 1 && (
            <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #2a3142' }}>
              <div style={{ fontSize: '0.75rem', color: '#a0a8b8', marginBottom: '8px' }}>Previous Versions:</div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {aiResponses.slice(1, 6).map((response, index) => (
                  <button
                    key={response.id}
                    onClick={() => setSelectedAiResponse(response)}
                    style={{
                      padding: '6px 12px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid #2a3142',
                      color: '#a0a8b8',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontFamily: 'Rajdhani, sans-serif',
                      fontSize: '0.75rem'
                    }}
                  >
                    Version {index + 2}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '15px' }}>
          <button
            onClick={handleRegenerateDescription}
            disabled={aiGenerating}
            style={{
              padding: '12px 16px',
              background: aiGenerating ? 'rgba(236, 72, 153, 0.2)' : 'rgba(236, 72, 153, 0.1)',
              border: aiGenerating ? '1px solid rgba(236, 72, 153, 0.5)' : '1px solid rgba(236, 72, 153, 0.3)',
              color: '#E0E6F0',
              borderRadius: '6px',
              cursor: aiGenerating ? 'not-allowed' : 'pointer',
              fontFamily: 'Rajdhani, sans-serif',
              fontWeight: '600',
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              opacity: aiGenerating ? 0.7 : 1,
              transition: 'all 0.2s ease'
            }}
          >
            {aiGenerating && <RefreshCw style={{ width: '14px', height: '14px' }} className="animate-spin" />}
            Regenerate Description
          </button>

          <button
            disabled
            style={{
              padding: '12px 16px',
              background: 'rgba(236, 72, 153, 0.05)',
              border: '1px solid rgba(107, 114, 128, 0.3)',
              color: '#9ca3af',
              borderRadius: '6px',
              cursor: 'not-allowed',
              fontFamily: 'Rajdhani, sans-serif',
              fontWeight: '600',
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              opacity: '0.5'
            }}
          >
            Send Images for Cleanup
            <span style={{
              background: 'rgba(236, 72, 153, 0.2)',
              color: '#f9a8d4',
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '0.65rem'
            }}>
              Coming Soon
            </span>
          </button>

          <button
            onClick={handleAIAnalyze}
            disabled={aiAnalyzing}
            style={{
              padding: '12px 16px',
              background: aiAnalyzing ? 'rgba(236, 72, 153, 0.2)' : 'rgba(236, 72, 153, 0.1)',
              border: aiAnalyzing ? '1px solid rgba(236, 72, 153, 0.5)' : '1px solid rgba(236, 72, 153, 0.3)',
              color: '#E0E6F0',
              borderRadius: '6px',
              cursor: aiAnalyzing ? 'not-allowed' : 'pointer',
              fontFamily: 'Rajdhani, sans-serif',
              fontWeight: '600',
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              opacity: aiAnalyzing ? 0.7 : 1,
              transition: 'all 0.2s ease'
            }}
          >
            {aiAnalyzing && <RefreshCw style={{ width: '14px', height: '14px' }} className="animate-spin" />}
            AI Analyze
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.75rem', color: '#a0a8b8' }}>
          <AlertCircle style={{ width: '12px', height: '12px' }} />
          <span>AI features use advanced models to analyze and enhance your item listings</span>
        </div>
      </div>

      {/* Pricing & Inventory */}
      <div style={{
        background: 'linear-gradient(to bottom right, rgba(17, 24, 39, 0.95), rgba(234, 179, 8, 0.05), rgba(17, 24, 39, 0.95))',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(234, 179, 8, 0.2)',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '20px',
        position: 'relative',
        boxShadow: '0 0 30px rgba(234, 179, 8, 0.2)',
        transition: 'all 0.3s ease'
      }}>
        {/* Corner Accent */}
        <div style={{ position: 'absolute', top: 0, right: 0, width: '128px', height: '128px', pointerEvents: 'none', background: 'linear-gradient(to bottom right, transparent, transparent, rgba(234, 179, 8, 0.1))' }} />

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '24px',
          paddingBottom: '16px',
          borderBottom: '1px solid rgba(234, 179, 8, 0.2)'
        }}>
          <div style={{ padding: '12px', borderRadius: '8px', background: 'rgba(234, 179, 8, 0.1)', border: '1px solid rgba(234, 179, 8, 0.3)', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
            <DollarSign style={{ width: '28px', height: '28px', color: '#eab308' }} />
          </div>
          <h2 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#fde047', margin: 0, letterSpacing: '0.025em' }}>Pricing & Inventory</h2>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontSize: '0.875rem', fontWeight: '600', color: 'white' }}>Target Price</label>
            <input
              type="number"
              step="0.01"
              value={isEditing ? formData.target_price || '' : item.target_price || ''}
              onChange={isEditing ? (e) => handleFieldChange('target_price', parseFloat(e.target.value)) : undefined}
              readOnly={!isEditing}
              style={{
                padding: '10px 12px',
                background: isEditing ? 'rgba(36, 41, 56, 0.8)' : 'rgba(36, 41, 56, 0.4)',
                border: '2px solid #2a3142',
                borderRadius: '6px',
                color: '#E0E6F0',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '0.875rem'
              }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontSize: '0.875rem', fontWeight: '600', color: 'white' }}>Cost</label>
            <input
              type="number"
              step="0.01"
              value={isEditing ? formData.cost || '' : item.cost || ''}
              onChange={isEditing ? (e) => handleFieldChange('cost', parseFloat(e.target.value)) : undefined}
              readOnly={!isEditing}
              style={{
                padding: '10px 12px',
                background: isEditing ? 'rgba(36, 41, 56, 0.8)' : 'rgba(36, 41, 56, 0.4)',
                border: '2px solid #2a3142',
                borderRadius: '6px',
                color: '#E0E6F0',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '0.875rem'
              }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontSize: '0.875rem', fontWeight: '600', color: 'white' }}>Quantity</label>
            <input
              type="number"
              value={isEditing ? formData.quantity || '' : item.quantity}
              onChange={isEditing ? (e) => handleFieldChange('quantity', parseInt(e.target.value)) : undefined}
              readOnly={!isEditing}
              style={{
                padding: '10px 12px',
                background: isEditing ? 'rgba(36, 41, 56, 0.8)' : 'rgba(36, 41, 56, 0.4)',
                border: '2px solid #2a3142',
                borderRadius: '6px',
                color: '#E0E6F0',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '0.875rem'
              }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontSize: '0.875rem', fontWeight: '600', color: 'white' }}>Reserved</label>
            <input
              type="number"
              value={item.reserved || 0}
              readOnly
              style={{
                padding: '10px 12px',
                background: 'rgba(36, 41, 56, 0.4)',
                border: '2px solid #2a3142',
                borderRadius: '6px',
                color: '#E0E6F0',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '0.875rem',
                cursor: 'not-allowed'
              }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontSize: '0.875rem', fontWeight: '600', color: 'white' }}>Available</label>
            <input
              type="number"
              value={item.quantity - (item.reserved || 0)}
              readOnly
              style={{
                padding: '10px 12px',
                background: 'rgba(36, 41, 56, 0.4)',
                border: '2px solid #2a3142',
                borderRadius: '6px',
                color: '#E0E6F0',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '0.875rem',
                cursor: 'not-allowed'
              }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontSize: '0.875rem', fontWeight: '600', color: 'white' }}>Potential Profit</label>
            <input
              type="text"
              value={`$${((item.target_price || 0) - (item.cost || 0)).toFixed(2)}`}
              readOnly
              style={{
                padding: '10px 12px',
                background: 'rgba(36, 41, 56, 0.4)',
                border: '2px solid #2a3142',
                borderRadius: '6px',
                color: '#10b981',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '0.875rem',
                fontWeight: '700',
                cursor: 'not-allowed'
              }}
            />
          </div>
        </div>
      </div>

      {/* Location */}
      <div style={{
        background: 'linear-gradient(to bottom right, rgba(17, 24, 39, 0.95), rgba(6, 182, 212, 0.05), rgba(17, 24, 39, 0.95))',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(6, 182, 212, 0.2)',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '20px',
        position: 'relative',
        boxShadow: '0 0 30px rgba(6, 182, 212, 0.2)',
        transition: 'all 0.3s ease'
      }}>
        {/* Corner Accent */}
        <div style={{ position: 'absolute', top: 0, right: 0, width: '128px', height: '128px', pointerEvents: 'none', background: 'linear-gradient(to bottom right, transparent, transparent, rgba(6, 182, 212, 0.1))' }} />

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '24px',
          paddingBottom: '16px',
          borderBottom: '1px solid rgba(6, 182, 212, 0.2)'
        }}>
          <div style={{ padding: '12px', borderRadius: '8px', background: 'rgba(6, 182, 212, 0.1)', border: '1px solid rgba(6, 182, 212, 0.3)', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
            <MapPin style={{ width: '28px', height: '28px', color: '#06b6d4' }} />
          </div>
          <h2 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#67e8f9', margin: 0, letterSpacing: '0.025em' }}>Location</h2>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Location</label>
            {isEditing ? (
              <select
                value={formData.location_id || ''}
                onChange={(e) => handleFieldChange('location_id', e.target.value)}
                style={{
                  padding: '10px 12px',
                  background: 'rgba(36, 41, 56, 0.8)',
                  border: '1px solid rgba(6, 182, 212, 0.4)',
                  borderRadius: '6px',
                  color: '#E0E6F0',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
              >
                <option value="">Select Location</option>
                {locationsData?.locations?.map((location: any) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={item.location_name || ''}
                readOnly
                style={{
                  padding: '10px 12px',
                  background: 'rgba(36, 41, 56, 0.4)',
                  border: '1px solid rgba(107, 114, 128, 0.3)',
                  borderRadius: '6px',
                  color: '#E0E6F0',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'not-allowed'
                }}
              />
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Shelf</label>
            <input
              type="text"
              value={item.shelf_name || 'N/A'}
              readOnly
              style={{
                padding: '10px 12px',
                background: 'rgba(36, 41, 56, 0.4)',
                border: '1px solid rgba(107, 114, 128, 0.3)',
                borderRadius: '6px',
                color: '#E0E6F0',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'not-allowed'
              }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tier</label>
            <input
              type="text"
              value={item.tier_position ? `Tier ${item.tier_position}${item.tier_nickname ? ` (${item.tier_nickname})` : ''}` : 'N/A'}
              readOnly
              style={{
                padding: '10px 12px',
                background: 'rgba(36, 41, 56, 0.4)',
                border: '1px solid rgba(107, 114, 128, 0.3)',
                borderRadius: '6px',
                color: '#E0E6F0',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'not-allowed'
              }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tote</label>
            <input
              type="text"
              value={item.tote_name || 'N/A'}
              readOnly
              style={{
                padding: '10px 12px',
                background: 'rgba(36, 41, 56, 0.4)',
                border: '1px solid rgba(107, 114, 128, 0.3)',
                borderRadius: '6px',
                color: '#E0E6F0',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'not-allowed'
              }}
            />
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div style={{
        background: 'linear-gradient(to bottom right, rgba(17, 24, 39, 0.95), rgba(139, 92, 246, 0.05), rgba(17, 24, 39, 0.95))',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(139, 92, 246, 0.2)',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '20px',
        position: 'relative',
        boxShadow: '0 0 30px rgba(139, 92, 246, 0.2)',
        transition: 'all 0.3s ease'
      }}>
        {/* Corner Accent */}
        <div style={{ position: 'absolute', top: 0, right: 0, width: '128px', height: '128px', pointerEvents: 'none', background: 'linear-gradient(to bottom right, transparent, transparent, rgba(139, 92, 246, 0.1))' }} />

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '24px',
          paddingBottom: '16px',
          borderBottom: '1px solid rgba(139, 92, 246, 0.2)'
        }}>
          <div style={{ padding: '12px', borderRadius: '8px', background: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.3)', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
            <Package style={{ width: '28px', height: '28px', color: '#8b5cf6' }} />
          </div>
          <h2 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#c4b5fd', margin: 0, letterSpacing: '0.025em' }}>Quick Actions</h2>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
          <button
            style={{
              padding: '10px 20px',
              background: '#242938',
              border: '2px solid #f97316',
              color: '#E0E6F0',
              borderRadius: '6px',
              cursor: 'pointer',
              fontFamily: 'Rajdhani, sans-serif',
              fontWeight: '600',
              fontSize: '0.875rem',
              textTransform: 'uppercase',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            Print Label
          </button>
          <button
            style={{
              padding: '10px 20px',
              background: '#242938',
              border: '2px solid #f97316',
              color: '#E0E6F0',
              borderRadius: '6px',
              cursor: 'pointer',
              fontFamily: 'Rajdhani, sans-serif',
              fontWeight: '600',
              fontSize: '0.875rem',
              textTransform: 'uppercase',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            Assign New IC
          </button>
          <button
            onClick={() => setShowMoveSlideout(true)}
            style={{
              padding: '10px 20px',
              background: '#242938',
              border: '2px solid #f97316',
              color: '#E0E6F0',
              borderRadius: '6px',
              cursor: 'pointer',
              fontFamily: 'Rajdhani, sans-serif',
              fontWeight: '600',
              fontSize: '0.875rem',
              textTransform: 'uppercase',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            Move Item
          </button>
          <button
            onClick={() => setExportModalOpen(true)}
            style={{
              padding: '10px 20px',
              background: '#242938',
              border: '2px solid #f97316',
              color: '#E0E6F0',
              borderRadius: '6px',
              cursor: 'pointer',
              fontFamily: 'Rajdhani, sans-serif',
              fontWeight: '600',
              fontSize: '0.875rem',
              textTransform: 'uppercase',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <Download style={{ width: '18px', height: '18px' }} />
            Export Data
          </button>
        </div>
      </div>

      {/* Sales History Placeholder */}
      <div style={{
        background: 'linear-gradient(to bottom right, rgba(17, 24, 39, 0.95), rgba(34, 197, 94, 0.05), rgba(17, 24, 39, 0.95))',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(34, 197, 94, 0.2)',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '20px',
        position: 'relative',
        boxShadow: '0 0 30px rgba(34, 197, 94, 0.2)',
        transition: 'all 0.3s ease'
      }}>
        {/* Corner Accent */}
        <div style={{ position: 'absolute', top: 0, right: 0, width: '128px', height: '128px', pointerEvents: 'none', background: 'linear-gradient(to bottom right, transparent, transparent, rgba(34, 197, 94, 0.1))' }} />

        <div
          onClick={() => setShowSalesHistory(!showSalesHistory)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            marginBottom: showSalesHistory ? '24px' : '0',
            paddingBottom: showSalesHistory ? '16px' : '0',
            borderBottom: showSalesHistory ? '1px solid rgba(34, 197, 94, 0.2)' : 'none',
            cursor: 'pointer'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ padding: '12px', borderRadius: '8px', background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
              <ShoppingCart style={{ width: '28px', height: '28px', color: '#22c55e' }} />
            </div>
            <h2 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#86efac', margin: 0, letterSpacing: '0.025em' }}>Sales History</h2>
          </div>
          <span style={{ color: '#86efac', fontSize: '1.5rem', transform: showSalesHistory ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▼</span>
        </div>
        {showSalesHistory && (
          salesHistoryLoading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              <RefreshCw style={{ width: '48px', height: '48px', margin: '0 auto 15px', opacity: '0.5', animation: 'spin 1s linear infinite' }} />
              <p>Loading sales history...</p>
            </div>
          ) : salesHistory.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              <TrendingUp style={{ width: '48px', height: '48px', margin: '0 auto 15px', opacity: '0.5' }} />
              <p>No sales history yet</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {salesHistory.map((sale: any) => {
                const isEditing = editingSaleId === sale.id
                const displayData = isEditing ? editingSaleData : sale
                const netProfit = Number(displayData.sale_price || 0) - (Number(displayData.marketplace_fees) || 0) - (Number(displayData.shipping_fees) || 0) - (item?.cost || 0)

                return (
                  <div key={sale.id} style={{
                    background: '#1a1f2e',
                    border: isEditing ? '2px solid #f97316' : '2px solid #2a3142',
                    borderRadius: '6px',
                    padding: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                        <ShoppingCart style={{ width: '18px', height: '18px', color: '#f97316' }} />
                        {isEditing ? (
                          <select
                            value={displayData.marketplace}
                            onChange={(e) => handleSaleFieldChange('marketplace', e.target.value)}
                            style={{
                              padding: '4px 8px',
                              background: '#242938',
                              border: '1px solid #f97316',
                              borderRadius: '4px',
                              color: '#ffffff',
                              fontSize: '14px',
                              fontWeight: 600
                            }}
                          >
                            <option value="">Select Marketplace</option>
                            <option value="eBay">eBay</option>
                            <option value="Amazon">Amazon</option>
                            <option value="Facebook Marketplace">Facebook Marketplace</option>
                            <option value="Poshmark">Poshmark</option>
                            <option value="Mercari">Mercari</option>
                            <option value="Craigslist">Craigslist</option>
                            <option value="Local Sale">Local Sale</option>
                            <option value="Other">Other</option>
                          </select>
                        ) : (
                          <span style={{ color: '#ffffff', fontWeight: 600 }}>{displayData.marketplace || 'Unknown'}</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {isEditing ? (
                          <input
                            type="date"
                            value={displayData.sale_date}
                            onChange={(e) => handleSaleFieldChange('sale_date', e.target.value)}
                            style={{
                              padding: '4px 8px',
                              background: '#242938',
                              border: '1px solid #f97316',
                              borderRadius: '4px',
                              color: '#ffffff',
                              fontSize: '14px'
                            }}
                          />
                        ) : (
                          <span style={{ color: '#9ca3af', fontSize: '14px' }}>{new Date(displayData.sale_date).toLocaleDateString()}</span>
                        )}
                        {!isEditing && (
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => handleEditSale(sale)}
                              style={{
                                padding: '4px 12px',
                                background: '#242938',
                                border: '1px solid #f97316',
                                borderRadius: '4px',
                                color: '#f97316',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <Edit style={{ width: '14px', height: '14px' }} />
                              Edit
                            </button>
                            <button
                              onClick={() => handleReturnFromSale(sale)}
                              style={{
                                padding: '4px 12px',
                                background: '#242938',
                                border: '1px solid #9D4EDD',
                                borderRadius: '4px',
                                color: '#9D4EDD',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <RotateCcw style={{ width: '14px', height: '14px' }} />
                              Return
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '14px' }}>
                      <div>
                        <span style={{ color: '#9ca3af' }}>Sale Price: </span>
                        {isEditing ? (
                          <input
                            type="number"
                            step="0.01"
                            value={displayData.sale_price}
                            onChange={(e) => handleSaleFieldChange('sale_price', e.target.value)}
                            style={{
                              width: '100px',
                              padding: '2px 6px',
                              background: '#242938',
                              border: '1px solid #f97316',
                              borderRadius: '4px',
                              color: '#10b981',
                              fontSize: '14px',
                              fontWeight: 600
                            }}
                          />
                        ) : (
                          <span style={{ color: '#10b981', fontWeight: 600 }}>${Number(displayData.sale_price || 0).toFixed(2)}</span>
                        )}
                      </div>
                      <div>
                        <span style={{ color: '#9ca3af' }}>Marketplace Fees: </span>
                        {isEditing ? (
                          <input
                            type="number"
                            step="0.01"
                            value={displayData.marketplace_fees}
                            onChange={(e) => handleSaleFieldChange('marketplace_fees', e.target.value)}
                            style={{
                              width: '100px',
                              padding: '2px 6px',
                              background: '#242938',
                              border: '1px solid #f97316',
                              borderRadius: '4px',
                              color: '#ef4444',
                              fontSize: '14px'
                            }}
                          />
                        ) : (
                          <span style={{ color: '#ef4444' }}>-${Number(displayData.marketplace_fees || 0).toFixed(2)}</span>
                        )}
                      </div>
                      <div>
                        <span style={{ color: '#9ca3af' }}>Shipping Fees: </span>
                        {isEditing ? (
                          <input
                            type="number"
                            step="0.01"
                            value={displayData.shipping_fees}
                            onChange={(e) => handleSaleFieldChange('shipping_fees', e.target.value)}
                            style={{
                              width: '100px',
                              padding: '2px 6px',
                              background: '#242938',
                              border: '1px solid #f97316',
                              borderRadius: '4px',
                              color: '#ef4444',
                              fontSize: '14px'
                            }}
                          />
                        ) : (
                          <span style={{ color: '#ef4444' }}>-${Number(displayData.shipping_fees || 0).toFixed(2)}</span>
                        )}
                      </div>
                      <div>
                        <span style={{ color: '#9ca3af' }}>Quantity: </span>
                        {isEditing ? (
                          <input
                            type="number"
                            min="1"
                            value={displayData.quantity_sold}
                            onChange={(e) => handleSaleFieldChange('quantity_sold', e.target.value)}
                            style={{
                              width: '60px',
                              padding: '2px 6px',
                              background: '#242938',
                              border: '1px solid #f97316',
                              borderRadius: '4px',
                              color: '#ffffff',
                              fontSize: '14px'
                            }}
                          />
                        ) : (
                          <span style={{ color: '#ffffff' }}>{displayData.quantity_sold || 1}</span>
                        )}
                      </div>
                    </div>

                    {isEditing && (
                      <div>
                        <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '4px' }}>Notes:</label>
                        <textarea
                          value={displayData.notes}
                          onChange={(e) => handleSaleFieldChange('notes', e.target.value)}
                          rows={2}
                          style={{
                            width: '100%',
                            padding: '6px 8px',
                            background: '#242938',
                            border: '1px solid #f97316',
                            borderRadius: '4px',
                            color: '#ffffff',
                            fontSize: '13px',
                            fontFamily: 'inherit',
                            resize: 'vertical'
                          }}
                        />
                      </div>
                    )}

                    <div style={{
                      borderTop: '1px solid #2a3142',
                      paddingTop: '12px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span style={{ color: '#9ca3af', fontSize: '14px' }}>Net Profit:</span>
                      <span style={{
                        color: netProfit >= 0 ? '#10b981' : '#ef4444',
                        fontWeight: 700,
                        fontSize: '18px'
                      }}>
                        ${netProfit.toFixed(2)}
                      </span>
                    </div>

                    {!isEditing && displayData.notes && (
                      <div style={{ fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>
                        Note: {displayData.notes}
                      </div>
                    )}

                    {isEditing && (
                      <div style={{ display: 'flex', gap: '8px', paddingTop: '8px' }}>
                        <button
                          onClick={() => handleSaveSale(sale.id)}
                          style={{
                            flex: 1,
                            padding: '8px 16px',
                            background: '#10b981',
                            border: 'none',
                            borderRadius: '4px',
                            color: '#ffffff',
                            fontSize: '14px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px'
                          }}
                        >
                          <Save style={{ width: '16px', height: '16px' }} />
                          Save Changes
                        </button>
                        <button
                          onClick={handleCancelEditSale}
                          style={{
                            flex: 1,
                            padding: '8px 16px',
                            background: '#ef4444',
                            border: 'none',
                            borderRadius: '4px',
                            color: '#ffffff',
                            fontSize: '14px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px'
                          }}
                        >
                          <X style={{ width: '16px', height: '16px' }} />
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )
        )}
      </div>

      {/* Returns History */}
      <div style={{
        background: 'linear-gradient(to bottom right, rgba(17, 24, 39, 0.95), rgba(157, 78, 221, 0.05), rgba(17, 24, 39, 0.95))',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(157, 78, 221, 0.2)',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '20px',
        position: 'relative',
        boxShadow: '0 0 30px rgba(157, 78, 221, 0.2)',
        transition: 'all 0.3s ease'
      }}>
        <div style={{ position: 'absolute', top: 0, right: 0, width: '128px', height: '128px', pointerEvents: 'none', background: 'linear-gradient(to bottom right, transparent, transparent, rgba(157, 78, 221, 0.1))' }} />

        <div
          onClick={() => setShowReturnsHistory(!showReturnsHistory)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            marginBottom: showReturnsHistory ? '24px' : '0',
            paddingBottom: showReturnsHistory ? '16px' : '0',
            borderBottom: showReturnsHistory ? '1px solid rgba(157, 78, 221, 0.2)' : 'none',
            cursor: 'pointer'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ padding: '12px', borderRadius: '8px', background: 'rgba(157, 78, 221, 0.1)', border: '1px solid rgba(157, 78, 221, 0.3)', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
              <RotateCcw style={{ width: '28px', height: '28px', color: '#9D4EDD' }} />
            </div>
            <h2 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#c4b5fd', margin: 0, letterSpacing: '0.025em' }}>Returns History</h2>
          </div>
          <span style={{ color: '#c4b5fd', fontSize: '1.5rem', transform: showReturnsHistory ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▼</span>
        </div>
        {showReturnsHistory && (
          returnsLoading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              <RefreshCw style={{ width: '48px', height: '48px', margin: '0 auto 15px', opacity: '0.5', animation: 'spin 1s linear infinite' }} />
              <p>Loading returns...</p>
            </div>
          ) : returns.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              <RotateCcw style={{ width: '48px', height: '48px', margin: '0 auto 15px', opacity: '0.5' }} />
              <p>No returns yet</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {returns.map((returnItem: any) => {
                const statusColors: Record<string, string> = {
                  pending: '#FF6B35',
                  approved: '#FFD700',
                  in_transit: '#9D4EDD',
                  received: '#00F0FF',
                  completed: '#00FF41',
                  rejected: '#FF006E',
                  cancelled: '#6b7280'
                }

                return (
                  <div key={returnItem.id} style={{
                    background: '#1a1f2e',
                    border: '2px solid #2a3142',
                    borderRadius: '6px',
                    padding: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                        <RotateCcw style={{ width: '18px', height: '18px', color: '#9D4EDD' }} />
                        <span style={{ color: '#ffffff', fontWeight: 600 }}>RMA: {returnItem.rma_number}</span>
                        <span style={{
                          padding: '2px 8px',
                          background: `${statusColors[returnItem.status] || '#6b7280'}22`,
                          border: `1px solid ${statusColors[returnItem.status] || '#6b7280'}`,
                          borderRadius: '4px',
                          color: statusColors[returnItem.status] || '#6b7280',
                          fontSize: '11px',
                          fontWeight: 600,
                          textTransform: 'uppercase'
                        }}>
                          {returnItem.status.replace('_', ' ')}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ color: '#9ca3af', fontSize: '14px' }}>
                          {new Date(returnItem.created_at).toLocaleDateString()}
                        </span>
                        <button
                          onClick={() => handleEditReturn(returnItem)}
                          style={{
                            background: 'rgba(0, 240, 255, 0.1)',
                            border: '1px solid rgba(0, 240, 255, 0.3)',
                            borderRadius: '4px',
                            padding: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(0, 240, 255, 0.2)'
                            e.currentTarget.style.borderColor = 'rgba(0, 240, 255, 0.5)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(0, 240, 255, 0.1)'
                            e.currentTarget.style.borderColor = 'rgba(0, 240, 255, 0.3)'
                          }}
                        >
                          <Edit2 style={{ width: 14, height: 14, color: '#00F0FF' }} />
                        </button>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '14px' }}>
                      <div>
                        <span style={{ color: '#9ca3af' }}>Customer: </span>
                        <span style={{ color: '#ffffff' }}>{returnItem.customer_name || 'N/A'}</span>
                      </div>
                      <div>
                        <span style={{ color: '#9ca3af' }}>Reason: </span>
                        <span style={{ color: '#ffffff' }}>{returnItem.return_reason}</span>
                      </div>
                      <div>
                        <span style={{ color: '#9ca3af' }}>Condition: </span>
                        <span style={{ color: '#ffffff' }}>{returnItem.condition}</span>
                      </div>
                      {returnItem.tracking_number && (
                        <div>
                          <span style={{ color: '#9ca3af' }}>Tracking: </span>
                          <span style={{ color: '#00F0FF' }}>{returnItem.tracking_number}</span>
                        </div>
                      )}
                    </div>

                    <div style={{
                      borderTop: '1px solid #2a3142',
                      paddingTop: '12px',
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '12px'
                    }}>
                      <div>
                        <span style={{ color: '#9ca3af', fontSize: '12px', display: 'block' }}>Net Impact (Loss)</span>
                        <span style={{
                          color: parseFloat(returnItem.net_impact || '0') < 0 ? '#FF006E' : '#00FF41',
                          fontWeight: 700,
                          fontSize: '16px'
                        }}>
                          ${parseFloat(returnItem.net_impact || '0').toFixed(2)}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: '#9ca3af', fontSize: '12px', display: 'block' }}>Potential Recovery</span>
                        <span style={{
                          color: '#00F0FF',
                          fontWeight: 700,
                          fontSize: '16px'
                        }}>
                          ${parseFloat(returnItem.potential_future_revenue || '0').toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )
        )}
      </div>

      {/* Actions History Placeholder */}
      <div style={{
        background: 'linear-gradient(to bottom right, rgba(17, 24, 39, 0.95), rgba(99, 102, 241, 0.05), rgba(17, 24, 39, 0.95))',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(99, 102, 241, 0.2)',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '20px',
        position: 'relative',
        boxShadow: '0 0 30px rgba(99, 102, 241, 0.2)',
        transition: 'all 0.3s ease'
      }}>
        {/* Corner Accent */}
        <div style={{ position: 'absolute', top: 0, right: 0, width: '128px', height: '128px', pointerEvents: 'none', background: 'linear-gradient(to bottom right, transparent, transparent, rgba(99, 102, 241, 0.1))' }} />

        <div
          onClick={() => setShowActionsHistory(!showActionsHistory)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            marginBottom: showActionsHistory ? '24px' : '0',
            paddingBottom: showActionsHistory ? '16px' : '0',
            borderBottom: showActionsHistory ? '1px solid rgba(99, 102, 241, 0.2)' : 'none',
            cursor: 'pointer'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ padding: '12px', borderRadius: '8px', background: 'rgba(99, 102, 241, 0.1)', border: '1px solid rgba(99, 102, 241, 0.3)', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
              <History style={{ width: '28px', height: '28px', color: '#6366f1' }} />
            </div>
            <h2 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#a5b4fc', margin: 0, letterSpacing: '0.025em' }}>Actions History</h2>
          </div>
          <span style={{ color: '#a5b4fc', fontSize: '1.5rem', transform: showActionsHistory ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▼</span>
        </div>
        {showActionsHistory && (
          actionsHistoryLoading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              <RefreshCw style={{ width: '48px', height: '48px', margin: '0 auto 15px', opacity: '0.5', animation: 'spin 1s linear infinite' }} />
              <p>Loading actions history...</p>
            </div>
          ) : actionsHistory.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              <Activity style={{ width: '48px', height: '48px', margin: '0 auto 15px', opacity: '0.5' }} />
              <p>No actions recorded yet</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {actionsHistory.map((action: any, index: number) => (
                <div key={index} style={{
                  background: '#1a1f2e',
                  border: '1px solid #2a3142',
                  borderLeft: `4px solid ${action.actionType === 'create' ? '#10b981' : action.actionType === 'update' ? '#3b82f6' : action.actionType === 'sold' ? '#f97316' : '#6b7280'}`,
                  borderRadius: '6px',
                  padding: '12px 16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    {action.actionType === 'create' && <Plus style={{ width: '18px', height: '18px', color: '#10b981' }} />}
                    {action.actionType === 'update' && <Edit style={{ width: '18px', height: '18px', color: '#3b82f6' }} />}
                    {action.actionType === 'sold' && <ShoppingCart style={{ width: '18px', height: '18px', color: '#f97316' }} />}
                    {action.actionType === 'move' && <Move style={{ width: '18px', height: '18px', color: '#8b5cf6' }} />}
                    <div style={{ flex: 1 }}>
                      <div style={{ color: '#ffffff', fontWeight: 600, fontSize: '14px', marginBottom: '2px' }}>
                        {action.action}
                      </div>
                      {action.details && (
                        <div style={{ color: '#9ca3af', fontSize: '13px' }}>
                          {action.details}
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ color: '#6b7280', fontSize: '12px', whiteSpace: 'nowrap' }}>
                    {new Date(action.timestamp).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {/* Bottom Action Bar */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: '#1a1f2e',
        borderTop: '4px solid #f97316',
        boxShadow: '0 -8px 30px rgba(249, 115, 22, 0.3)',
        padding: '20px',
        zIndex: 1000
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => navigate('/search')}
              style={{
                padding: '10px 20px',
                background: '#242938',
                border: '2px solid #06b6d4',
                color: '#E0E6F0',
                borderRadius: '6px',
                cursor: 'pointer',
                fontFamily: 'Rajdhani, sans-serif',
                fontWeight: '600',
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              Back
            </button>
            <button
              onClick={() => isEditing ? handleCancel() : setIsEditing(true)}
              style={{
                padding: '10px 20px',
                background: '#242938',
                border: '2px solid #f97316',
                color: '#E0E6F0',
                borderRadius: '6px',
                cursor: 'pointer',
                fontFamily: 'Rajdhani, sans-serif',
                fontWeight: '600',
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {isEditing ? 'Cancel' : 'Edit'}
            </button>
            <button
              onClick={() => setShowMoveSlideout(true)}
              style={{
                padding: '10px 20px',
                background: '#242938',
                border: '2px solid #f97316',
                color: '#E0E6F0',
                borderRadius: '6px',
                cursor: 'pointer',
                fontFamily: 'Rajdhani, sans-serif',
                fontWeight: '600',
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              Move
            </button>
            <button
              onClick={handleDuplicate}
              style={{
                padding: '10px 20px',
                background: '#242938',
                border: '2px solid #8b5cf6',
                color: '#E0E6F0',
                borderRadius: '6px',
                cursor: 'pointer',
                fontFamily: 'Rajdhani, sans-serif',
                fontWeight: '600',
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Copy style={{ width: '16px', height: '16px' }} />
              Duplicate
            </button>
            <button
              onClick={() => setShowSoldSlideout(true)}
              style={{
                padding: '10px 20px',
                background: '#242938',
                border: '2px solid #f97316',
                color: '#E0E6F0',
                borderRadius: '6px',
                cursor: 'pointer',
                fontFamily: 'Rajdhani, sans-serif',
                fontWeight: '600',
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              Mark Sold
            </button>
            <button
              onClick={() => setShowListedSlideout(true)}
              style={{
                padding: '10px 20px',
                background: '#242938',
                border: '2px solid #22D3EE',
                color: '#E0E6F0',
                borderRadius: '6px',
                cursor: 'pointer',
                fontFamily: 'Rajdhani, sans-serif',
                fontWeight: '600',
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Tag style={{ width: 16, height: 16 }} />
              Mark Listed
            </button>
            <button
              onClick={handleReturnItem}
              style={{
                padding: '10px 20px',
                background: '#242938',
                border: '2px solid #9D4EDD',
                color: '#E0E6F0',
                borderRadius: '6px',
                cursor: 'pointer',
                fontFamily: 'Rajdhani, sans-serif',
                fontWeight: '600',
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <RotateCcw style={{ width: 16, height: 16 }} />
              Return Item
            </button>
            <PrintButton
              entityType="item"
              entityData={item}
              categoryId={item?.category_id}
              variant="compact"
              label="Print Label"
            />
          </div>
          {isEditing && (
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleSave}
                style={{
                  padding: '10px 20px',
                  background: '#242938',
                  border: '2px solid #f97316',
                  color: '#E0E6F0',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontWeight: '600',
                  fontSize: '0.875rem',
                  textTransform: 'uppercase',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                Save Changes
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Slideouts */}
      <MoveSlideout
        isOpen={showMoveSlideout}
        onClose={() => setShowMoveSlideout(false)}
        itemData={item ? {
          id: item.id,
          name: item.name,
          ic: item.ic,
          currentLocation: item.location_name || 'Unknown',
          currentLocationId: item.location_id,
          currentShelf: item.shelf_name || 'N/A',
          currentTier: item.tier_position ? `Tier ${item.tier_position}` : 'N/A',
          currentTote: item.tote_name || 'N/A'
        } : undefined}
        onMoveComplete={() => {
          setShowMoveSlideout(false)
          window.location.reload() // Reload to show updated location
        }}
      />

      <SoldSlideout
        isOpen={showSoldSlideout}
        onClose={() => setShowSoldSlideout(false)}
        itemData={item ? {
          id: item.id,
          name: item.name,
          ic: item.ic,
          targetPrice: item.target_price,
          cost: item.cost
        } : undefined}
        onSaleComplete={() => {
          setShowSoldSlideout(false)
          window.location.reload()
        }}
      />

      <PicklistSlideout
        isOpen={showPicklistSlideout}
        onClose={() => setShowPicklistSlideout(false)}
        itemData={item ? {
          id: item.id,
          name: item.name,
          ic: item.ic,
          location: item.location_name || 'Unknown',
          tote: item.tote_name || 'Unknown'
        } : undefined}
        onPicklistComplete={() => setShowPicklistSlideout(false)}
      />

      {/* Return Item Modal */}
      {item && (
        <ReturnItemModal
          isOpen={showReturnModal}
          onClose={() => {
            setShowReturnModal(false)
            setReturnModalSale(null)
            setEditingReturn(null)
          }}
          item={{
            id: item.id,
            name: item.name,
            sku: item.sku
          }}
          sale={returnModalSale}
          existingReturn={editingReturn}
          onSuccess={handleReturnSuccess}
        />
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        itemIds={item?.ic ? [item.ic] : []}
        itemCount={1}
      />

      {/* Listed Slideout */}
      {item && (
        <ListedSlideout
          isOpen={showListedSlideout}
          onClose={() => setShowListedSlideout(false)}
          itemId={item.id}
          itemName={item.name}
          onSuccess={fetchListings}
        />
      )}

      {/* Image Enhancement Modal */}
      {enhanceModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
          onClick={() => !enhancing && setEnhanceModalOpen(false)}
        >
          <div
            style={{
              background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
              borderRadius: '12px',
              padding: '32px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '2px solid rgba(16, 185, 129, 0.3)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#10B981', margin: 0 }}>
                <Activity style={{ width: '24px', height: '24px', display: 'inline', marginRight: '12px' }} />
                AI Image Enhancement
              </h2>
              {!enhancing && (
                <button
                  onClick={() => setEnhanceModalOpen(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#6b7280',
                    cursor: 'pointer',
                    padding: '8px'
                  }}
                >
                  <X style={{ width: '24px', height: '24px' }} />
                </button>
              )}
            </div>

            <p style={{ color: '#8A95AA', marginBottom: '24px', fontSize: '0.875rem' }}>
              Select images to enhance with AI-powered auto-crop, lighting improvements, and optional background removal.
              Processed images will be added as new images to this item.
            </p>

            {/* Enhancement Options */}
            <div style={{ marginBottom: '24px', padding: '20px', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '8px', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#10B981', marginBottom: '16px' }}>Enhancement Options</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#E0E6F0', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={enhanceOptions.autoCrop}
                    onChange={(e) => setEnhanceOptions({ ...enhanceOptions, autoCrop: e.target.checked })}
                    disabled={enhancing}
                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                  />
                  <span style={{ fontWeight: '600' }}>Auto-Crop</span>
                  <span style={{ color: '#8A95AA', fontSize: '0.875rem' }}>Remove whitespace and borders</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#E0E6F0', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={enhanceOptions.enhance}
                    onChange={(e) => setEnhanceOptions({ ...enhanceOptions, enhance: e.target.checked })}
                    disabled={enhancing}
                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                  />
                  <span style={{ fontWeight: '600' }}>Enhance Lighting</span>
                  <span style={{ color: '#8A95AA', fontSize: '0.875rem' }}>Improve brightness, contrast, and color</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#E0E6F0', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={enhanceOptions.removeBg}
                    onChange={(e) => setEnhanceOptions({ ...enhanceOptions, removeBg: e.target.checked })}
                    disabled={enhancing}
                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                  />
                  <span style={{ fontWeight: '600' }}>Remove Background</span>
                  <span style={{ color: '#8A95AA', fontSize: '0.875rem' }}>AI-powered background removal (takes 5-10 seconds per image)</span>
                </label>

                {/* Background options - only show when Remove Background is enabled */}
                {enhanceOptions.removeBg && (
                  <div style={{ marginLeft: '32px', display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '8px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#E0E6F0', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={enhanceOptions.bgTransparent}
                        onChange={(e) => setEnhanceOptions({ ...enhanceOptions, bgTransparent: e.target.checked })}
                        disabled={enhancing}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span style={{ fontWeight: '500', fontSize: '0.9rem' }}>Transparent Background</span>
                      <span style={{ color: '#8A95AA', fontSize: '0.8rem' }}>(PNG format)</span>
                    </label>

                    {!enhanceOptions.bgTransparent && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontWeight: '500', fontSize: '0.9rem', color: '#E0E6F0' }}>Background Color:</span>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {['white', 'black', 'gray'].map((color) => (
                            <label key={color} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                              <input
                                type="radio"
                                name="bgColor"
                                value={color}
                                checked={enhanceOptions.bgColor === color}
                                onChange={(e) => setEnhanceOptions({ ...enhanceOptions, bgColor: e.target.value })}
                                disabled={enhancing}
                                style={{ cursor: 'pointer' }}
                              />
                              <span style={{ fontSize: '0.85rem', color: '#E0E6F0', textTransform: 'capitalize' }}>{color}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Image Selection */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#E0E6F0', marginBottom: '12px' }}>
                Select Images ({selectedImagesForEnhancement.length} selected)
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '12px' }}>
                {images.map((img: any) => {
                  const isSelected = selectedImagesForEnhancement.includes(img.id)
                  return (
                    <div
                      key={img.id}
                      onClick={(e) => {
                        e.stopPropagation()
                        if (enhancing) return
                        if (isSelected) {
                          setSelectedImagesForEnhancement(selectedImagesForEnhancement.filter(id => id !== img.id))
                        } else {
                          setSelectedImagesForEnhancement([...selectedImagesForEnhancement, img.id])
                        }
                      }}
                      style={{
                        position: 'relative',
                        aspectRatio: '1',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        cursor: enhancing ? 'default' : 'pointer',
                        border: isSelected ? '3px solid #10B981' : '2px solid rgba(255, 255, 255, 0.1)',
                        opacity: enhancing ? 0.5 : 1,
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <img
                        src={getImageUrl(img.thumbnail_url || img.url)}
                        alt={img.filename}
                        onError={(e) => {
                          const imgEl = e.currentTarget;
                          console.error('❌ Enhance modal image failed:', imgEl.src);
                          if (imgEl.src !== '/placeholder.png') {
                            imgEl.src = '/placeholder.png';
                          }
                        }}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                      {isSelected && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            background: '#10B981',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: '700',
                            fontSize: '0.875rem'
                          }}
                        >
                          ✓
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setEnhanceModalOpen(false)}
                disabled={enhancing}
                style={{
                  padding: '12px 24px',
                  background: 'rgba(107, 114, 128, 0.1)',
                  border: '2px solid rgba(107, 114, 128, 0.3)',
                  color: '#9CA3AF',
                  borderRadius: '6px',
                  cursor: enhancing ? 'not-allowed' : 'pointer',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontWeight: '600',
                  fontSize: '0.875rem',
                  textTransform: 'uppercase',
                  opacity: enhancing ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleEnhanceImages}
                disabled={enhancing || selectedImagesForEnhancement.length === 0}
                style={{
                  padding: '12px 24px',
                  background: enhancing || selectedImagesForEnhancement.length === 0 ? 'rgba(16, 185, 129, 0.3)' : 'rgba(16, 185, 129, 0.2)',
                  border: '2px solid rgba(16, 185, 129, 0.5)',
                  color: '#10B981',
                  borderRadius: '6px',
                  cursor: enhancing || selectedImagesForEnhancement.length === 0 ? 'not-allowed' : 'pointer',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontWeight: '600',
                  fontSize: '0.875rem',
                  textTransform: 'uppercase',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: enhancing || selectedImagesForEnhancement.length === 0 ? 0.5 : 1
                }}
              >
                {enhancing ? (
                  <>
                    <RefreshCw style={{ width: '16px', height: '16px' }} className="animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Activity style={{ width: '16px', height: '16px' }} />
                    Enhance {selectedImagesForEnhancement.length} Image{selectedImagesForEnhancement.length !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
